import { SaleProforma } from "@towersystems/roam-common/lib/generated-types";
import { QUERY_SALE_PROFORMAS } from "../../../../utilities/proformas/constants";
import { useLoadMoreList } from "../../../../utilities/load-more-list";
import React from "react";
import { useSearch } from "../../../../utilities/search";
import { FlatListSearchableSkeleton } from "../../../../components/FlatListSearchableSkeleton";
import { useScheduler } from "../../../../utilities/scheduler";
import {
  DEFAULT_PAGE_WIDTH,
  Divider,
  FlatActionList,
  FlatListSearchable,
  Page,
  Stack,
  Text,
  useWithAppState,
} from "@towersystems/roam-common-native";
import { useTranslation } from "../../../../utilities/i18n";
import { Spacings, View } from "react-native-ui-lib";
import { StackScreenProps } from "@react-navigation/stack";
import { ActionCreateProformaSaleRootParamList } from "../../../../types";
import {
  formatDateToCalendar,
  fullDateTimeFormat,
  moneyToFormattedString,
} from "@towersystems/roam-common/lib/shared-utils";
import { AntDesign, EvilIcons } from "@expo/vector-icons";

export interface ListProformasScreenProps
  extends StackScreenProps<
    ActionCreateProformaSaleRootParamList,
    "ListProformasScreen"
  > {}

export const ListProformasScreen = ({
  navigation,
}: ListProformasScreenProps) => {
  const { t } = useTranslation("pos");

  const { schedule } = useScheduler(500);

  const search = useSearch();

  const {
    appState: { locationId },
  } = useWithAppState();

  const loadMoreList = useLoadMoreList<SaleProforma>({
    initialLoad: true,
    query: QUERY_SALE_PROFORMAS,
    resultKey: "saleProformas",
    sort: { updatedAt: "DESC" },
    variableFormatter: (variables) => {
      return {
        options: {
          ...variables,
          filter: {
            identifier: { contains: search.query ?? undefined },
            saleId: { isNull: true },
            locationId: { eq: locationId },
          },
        },
      };
    },
  });

  const handleLoadMore = (reset?: boolean) => {
    loadMoreList.loadMore(
      {
        filter: {
          identifier: { contains: search.query ?? undefined },
          saleId: { isNull: true },
          locationId: { eq: locationId },
        },
      },
      reset
    );
  };

  React.useEffect(() => {
    if (loadMoreList.called) {
      schedule(() => handleLoadMore(true));
    }
  }, [search.query]);

  const renderItem = React.useCallback(({ item }: { item: SaleProforma }) => {
    return (
      <FlatActionList.RenderItem
        item={{
          key: item.id,
          title: item.data?.quoteStamp ?? "",
          titleContent({ title, subTitle }) {
            return (
              <View>
                {title && <Text variant="heading1">#{title}</Text>}
                {item.data?.lockedDate && (
                  <View>
                    {item.location?.name ? (
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          marginBottom: Spacings.s1,
                        }}
                      >
                        <EvilIcons
                          name="location"
                          size={18}
                          color="black"
                          style={{
                            marginRight: Spacings.s1,
                          }}
                        />
                        <Text variant="heading5" fontWeight="bold">
                          {item.location?.name}
                        </Text>
                      </View>
                    ) : null}
                    <View
                      style={{ flexDirection: "row", alignItems: "center" }}
                    >
                      <AntDesign
                        name="lock"
                        size={15}
                        color="black"
                        style={{
                          marginRight: Spacings.s1,
                        }}
                      />
                      <Text color="subdued">
                        {formatDateToCalendar(
                          item.data?.lockedDate,
                          fullDateTimeFormat
                        )}
                      </Text>
                    </View>
                  </View>
                )}
                {item.data?.customerDetails && (
                  <View
                    style={{
                      marginTop: Spacings.s2,
                    }}
                  >
                    <Text>{item.data?.customerDetails?.customerName}</Text>
                  </View>
                )}
                {item.data?.invoiceNotes && (
                  <View>
                    <Text color="subdued" variant="caption">
                      "{item.data?.invoiceNotes}"
                    </Text>
                  </View>
                )}
              </View>
            );
          },
          onPress: () => {
            navigation.navigate("ViewProformaScreen", {
              proformaId: item.id,
            });
          },
          componentRight(icon) {
            return (
              <View
                style={{ alignContent: "center", justifyContent: "center" }}
              >
                <Stack spacing={Spacings.s1}>
                  <View style={{ alignItems: "flex-end" }}>
                    <Text variant="heading1" fontWeight="bold">
                      {moneyToFormattedString(item.data?.balance ?? 0)}
                    </Text>
                    <Text textAlign="right">{t("total_balance")}</Text>
                    <Text color="subdued" textAlign="right">
                      {t("formatted_quantity", {
                        quantity: item.data?.items.length ?? 0,
                      })}
                    </Text>
                  </View>
                  <View
                    style={{ alignContent: "center", justifyContent: "center" }}
                  >
                    {icon}
                  </View>
                </Stack>
              </View>
            );
          },
        }}
      />
    );
  }, []);

  const items: SaleProforma[] = loadMoreList.items || [];

  if (loadMoreList.loading && loadMoreList.items.length == 0) {
    return <FlatListSearchableSkeleton />;
  }

  console.log("items", items);

  return (
    <Page padding={0} fullWidth title={t("name")} scrollable={false}>
      <FlatListSearchable
        contentContainerStyle={{
          maxWidth: DEFAULT_PAGE_WIDTH,
          width: "100%",
          alignSelf: "center",
          paddingHorizontal: Spacings.s5,
          paddingTop: Spacings.s5,
        }}
        placeholder="Search..."
        data={items}
        onChange={(query) => search.setQuery(query)}
        value={search.query}
        ItemSeparatorComponent={Divider}
        renderItem={renderItem}
        scrollEnabled={true}
        nestedScrollEnabled={true}
        onEndReachedThreshold={0.3}
        onEndReached={() => {
          items.length >= 1 && handleLoadMore(false);
        }}
      />
    </Page>
  );
};
